<script setup lang="ts">
  import { computed } from 'vue';
  import { useModal } from 'vue-final-modal';
  import { List } from 'lucide-vue-next';
  import { usePropertyForm } from '../../../model/usePropertyForm';
  import type { CheckboxProperties as CheckboxPropsType } from '../../../model/form.types';
  import {
    type CheckboxFormValues,
    checkboxValidationSchema,
    extractCheckboxInitialValues,
    mapCheckboxValuesToProperties,
  } from '../../../model/useCheckboxPropertyForm';
  import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
  } from '@/shared/ui';
  import CheckboxAlternativesModal from './CheckboxAlternativesModal.vue';
  import { FORM_FIELD_CHARACTER_LIMITS } from '../../../lib/form-field-character-limits';

  interface Props {
    elementId?: string;
  }

  const props = defineProps<Props>();

  const { selectedElement } = usePropertyForm<CheckboxFormValues, CheckboxPropsType>({
    elementId: () => props.elementId,
    validationSchema: checkboxValidationSchema,
    extractInitialValues: extractCheckboxInitialValues,
    mapValuesToProperties: mapCheckboxValuesToProperties,
  });

  const alternativesCount = computed(
    () => (selectedElement.value?.properties as CheckboxPropsType)?.options?.length ?? 0
  );

  const alternativesModal = useModal({
    component: CheckboxAlternativesModal,
    attrs: {
      elementId: props.elementId ?? '',
      onClose() {
        alternativesModal.close();
      },
    },
  });
</script>

<template>
  <div class="flex flex-col">
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="name"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            ID do campo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="nome_do_campo"
              class="h-11 text-sm"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.FIELD_ID"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="placeholder"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Texto auxiliar
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui o texto auxiliar"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.AUXILIARY_TEXT"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div class="border-b border-gray-02 py-4 flex flex-col gap-[10px]">
      <FormField
        v-slot="{ componentField }"
        name="label"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Rótulo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui"
              class="h-[42px]"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.LABEL"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField
        v-slot="{ componentField }"
        name="labelPosition"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Posicionamento
          </FormLabel>
          <Select v-bind="componentField">
            <FormControl>
              <SelectTrigger class="h-11 w-full">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="top">
                Acima do campo
              </SelectItem>
              <SelectItem value="left">
                À Esquerda do Campo
              </SelectItem>
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="required"
      >
        <FormItem class="flex items-center justify-between h-5">
          <FormLabel class="text-sm text-gray-07 font-normal cursor-pointer">
            Obrigatória
          </FormLabel>
          <FormControl>
            <Switch v-bind="componentField" />
          </FormControl>
        </FormItem>
      </FormField>
    </div>

    <div class="flex gap-2 items-center h-[42px] py-2 border-b border-gray-03 mt-4">
      <div class="bg-gray-03 flex items-center justify-center p-0.5 rounded shrink-0 size-6">
        <List
          :size="18"
          class="text-white-01"
        />
      </div>
      <span class="text-sm text-gray-07">Alternativas</span>
    </div>

    <div class="py-4 flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <button
          type="button"
          class="w-full h-[42px] bg-[var(--theme-secondary)] text-white text-sm font-medium rounded-lg flex items-center justify-center shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
          @click="alternativesModal.open()"
        >
          Editar alternativas
        </button>
        <p class="text-[10px]">
          <span class="text-gray-07">Total de alternativas: </span>
          <span class="text-gray-03">{{ alternativesCount }}</span>
        </p>
      </div>

      <FormField
        v-slot="{ componentField }"
        name="multipleSelection"
      >
        <FormItem class="flex items-center justify-between h-5">
          <FormLabel class="text-sm text-gray-07 font-normal cursor-pointer">
            Seleção múltipla
          </FormLabel>
          <FormControl>
            <Switch v-bind="componentField" />
          </FormControl>
        </FormItem>
      </FormField>

      <FormField
        v-slot="{ componentField }"
        name="orientation"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Organização
          </FormLabel>
          <Select v-bind="componentField">
            <FormControl>
              <SelectTrigger class="h-11 w-full">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="horizontal">
                Horizontal
              </SelectItem>
              <SelectItem value="vertical">
                Vertical
              </SelectItem>
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>
  </div>

  <FormField name="options">
    <FormMessage />
  </FormField>
</template>
